/* ==========================================================================
   garage.js

   Turns a link in Shopify's account menu into a trigger that opens your Garage.

   Setup
   1. In the Customer account main menu, add a link named "Garage" whose URL
      is your store's home page followed by #garage, for example
      https://your-store.com/#garage
   2. Put the Garage in the theme (theme.liquid or a section) as
      <dialog class="dialog" id="garage"> ... </dialog>
      styled by shopify-account-garage.css. Give its close button the
      attribute data-garage-close.
   3. Load this file on every page.

   What happens on click
   - The click is caught before the browser follows the link.
   - Shopify's account sheet is closed by pressing its own close button.
   - When Shopify reports the sheet closed, the Garage opens in the
     same position.

   Any other <a href="...#garage"> on the page opens the Garage as well.

   This file also contains the MyGarage save / remove logic (section 4,
   formerly mygarage.js) and the year / make / model finder (section 5).

   If the click can't be caught (for example the link is used from the
   customer account pages on the account. subdomain), the link simply goes
   to the home page with #garage, and the Garage opens on load.
   ========================================================================== */
(function () {
  var HASH = '#garage';
  var GARAGE_ID = 'garage';
  var resetFinder = null; // set by the finder (section 5); puts it back to just "Add a motorcycle"
  var CLOSE_WAIT = 400; // ms to wait for Shopify's close event before opening anyway
  var ACCOUNT_TOKENS = [
    'dialog-position-top', 'font-heading', 'font-heading-weight', 'font-heading-transform',
    'font-body', 'font-body-weight', 'font-body-transform', 'radius-base', 'radius-dialog',
    'radius-button', 'radius-button-small', 'radius-input', 'color-background', 'color-text',
    'color-accent', 'color-accent-text', 'color-accent-hover', 'color-border',
    'color-background-subdued', 'color-text-subdued', 'color-shadow',
    'color-control-background', 'color-control-text', 'color-card-background', 'color-card-text'
  ];

  // A theme can render more than one <shopify-account> (for example one in the
  // desktop header and another in the mobile header), so never assume the first.
  function getAccountHosts() {
    return Array.prototype.slice.call(document.querySelectorAll('shopify-account'));
  }

  // The one the customer can actually see at this screen size.
  function getVisibleAccountHost() {
    var hosts = getAccountHosts();
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getClientRects().length) return hosts[i];
    }
    return hosts[0] || null;
  }

  function getGarage() {
    return document.getElementById(GARAGE_ID);
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function openGarage() {
    var garage = getGarage();
    if (!garage || garage.open) return;

    // Match Shopify's account sheet: copy every --shopify-account-* value in effect
    // on <shopify-account> (fonts, colours, radii, top offset) onto the Garage.
    var host = getVisibleAccountHost();
    if (host) {
      var hostStyles = getComputedStyle(host);
      ACCOUNT_TOKENS.forEach(function (name) {
        var value = hostStyles.getPropertyValue('--shopify-account-' + name).trim();
        if (value) garage.style.setProperty('--shopify-account-' + name, value);
      });
    }

    garage.classList.remove('closing');
    initFinder(garage);
    if (resetFinder) resetFinder();
    garage.showModal();
  }

  // Same exit animation Shopify uses: add .closing, close when it finishes.
  function closeGarage(garage, then) {
    if (!garage.open || garage.classList.contains('closing')) return;
    if (reducedMotion()) { garage.close(); if (resetFinder) resetFinder(); if (then) then(); return; }
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      garage.removeEventListener('animationend', onEnd);
      garage.classList.remove('closing');
      garage.close();
      if (resetFinder) resetFinder();
      if (then) then();
    }
    function onEnd(event) {
      if (event.target === garage) finish();
    }
    garage.addEventListener('animationend', onEnd);
    setTimeout(finish, CLOSE_WAIT);
    garage.classList.add('closing');
  }

  // Close every open Shopify account sheet through its own close button, then
  // run `then`. Checks all <shopify-account> elements, not just the first.
  function closeAccountSheet(then) {
    var open = [];
    getAccountHosts().forEach(function (host) {
      var root = host.shadowRoot;
      var sheet = root && root.querySelector('dialog');
      if (sheet && sheet.open) {
        open.push({ host: host, sheet: sheet, button: root.querySelector('#close-button') });
      }
    });
    if (!open.length) { then(); return; }

    var done = false;
    function go() {
      if (done) return;
      done = true;
      open.forEach(function (item) {
        item.host.removeEventListener('close', onClose);
        // Backstop: never leave a sheet open underneath the Garage.
        if (item.sheet.open) item.sheet.close();
      });
      then();
    }
    function onClose() {
      var stillOpen = open.some(function (item) { return item.sheet.open; });
      if (!stillOpen) go();
    }

    open.forEach(function (item) {
      item.host.addEventListener('close', onClose); // documented event: sheet closed
      if (item.button) item.button.click();
    });
    setTimeout(go, CLOSE_WAIT);
  }

  // 1. Catch clicks on any link ending in #garage, including links inside
  //    the account sheet (composedPath sees through its shadow root).
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!getGarage()) return; // no Garage on this page: let the link navigate

    var path = event.composedPath ? event.composedPath() : [];
    var link = null;
    for (var i = 0; i < path.length; i++) {
      if (path[i].tagName === 'A' && path[i].hash === HASH) { link = path[i]; break; }
    }
    if (!link) return;

    event.preventDefault();
    closeAccountSheet(openGarage);
  }, true);

  // 2. Fallback: arrived on, or moved to, a URL ending in #garage.
  function openFromHash() {
    if (location.hash !== HASH || !getGarage()) return;
    history.replaceState(null, '', location.pathname + location.search);
    openGarage();
  }
  // The hash matches id="garage", so on a fresh load the browser also tries
  // to scroll to the (hidden) dialog. Put the page back at the top.
  function openFromHashOnLoad() {
    if (location.hash !== HASH || !getGarage()) return;
    openFromHash();
    window.scrollTo(0, 0);
    window.addEventListener('load', function () { window.scrollTo(0, 0); }, { once: true });
  }
  window.addEventListener('hashchange', openFromHash);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHashOnLoad);
  } else {
    openFromHashOnLoad();
  }

  // Shopify's own account button (the header avatar), inside the component.
  function getAccountButton() {
    var host = getVisibleAccountHost();
    var root = host && host.shadowRoot;
    return root ? root.querySelector('button.account-button, [part~="signed-out-avatar"]') : null;
  }

  // 3. Closing the Garage: close button, click outside, Esc.
  //    Also the signed-out "Sign in" button: close the Garage, then open
  //    Shopify's account sheet. If the account button can't be reached, the
  //    link is left alone and goes to the login page instead.
  document.addEventListener('click', function (event) {
    var garage = getGarage();
    if (!garage || !garage.open) return;

    var signIn = event.target.closest && event.target.closest('[data-garage-sign-in]');
    if (signIn && garage.contains(signIn)) {
      var accountButton = getAccountButton();
      if (!accountButton) return;
      event.preventDefault();
      closeGarage(garage, function () { accountButton.click(); });
      return;
    }
    if (event.target === garage) { closeGarage(garage); return; }
    var closer = event.target.closest && event.target.closest('[data-garage-close]');
    if (closer && garage.contains(closer)) closeGarage(garage);
  });

  document.addEventListener('cancel', function (event) {
    var garage = getGarage();
    if (garage && event.target === garage) {
      event.preventDefault();
      closeGarage(garage);
    }
  }, true);

  // 4. MyGarage: save / remove through /apps/mygarage/save and /remove.
  //    (Formerly mygarage.js, merged in unchanged apart from the account-sheet
  //    classes on newly created rows. Do not load the old mygarage.js as well,
  //    or every save and remove will be sent twice.)

  function getGarageLists() {
    var lists = Array.from(document.querySelectorAll('[data-mygarage-list], #MyGarageList'));
    return lists.filter(function(list, index) {
      return lists.indexOf(list) === index;
    });
  }

  function getGarageEmptyStates() {
    var empties = Array.from(document.querySelectorAll('[data-mygarage-empty], #MyGarageEmpty'));
    return empties.filter(function(empty, index) {
      return empties.indexOf(empty) === index;
    });
  }

  function getRemoveIconSVG() {
    return '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5L15 15M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
  }

  function isListRemoveButton(button) {
    return !!button.closest('[data-mygarage-item]');
  }

  function setButtonToRemoveState(button) {
    var listButton = isListRemoveButton(button);

    button.setAttribute('data-mygarage-action', 'remove');
    button.disabled = false;
    button.style.width = '';
    button.style.opacity = '';

    if (listButton) {
      button.classList.add('mygarage-remove-btn');
      button.setAttribute('aria-label', 'Remove from MyGarage');
      button.innerHTML = getRemoveIconSVG();
    } else {
      button.classList.remove('mygarage-remove-btn');
      button.removeAttribute('aria-label');
      button.textContent = 'Remove from MyGarage';
    }
  }

  function setButtonToSaveState(button) {
    button.setAttribute('data-mygarage-action', 'save');
    button.disabled = false;
    button.style.width = '';
    button.style.opacity = '';
    button.classList.remove('mygarage-remove-btn');
    button.removeAttribute('aria-label');
    button.textContent = 'Save to MyGarage';
  }

  function setButtonLoadingState(button, action) {
    var listButton = isListRemoveButton(button);
    var width = button.offsetWidth;

    button.style.width = width + 'px';
    button.disabled = true;

    if (action === 'remove') {
      if (listButton) {
        button.classList.add('mygarage-remove-btn');
        button.setAttribute('aria-label', 'Removing from MyGarage');
        button.innerHTML = getRemoveIconSVG();
        button.style.opacity = '0.45';
      } else {
        button.classList.remove('mygarage-remove-btn');
        button.removeAttribute('aria-label');
        button.innerHTML = '<span class="mygarage-loading-text">Removing...</span>';
      }
    } else {
      button.classList.remove('mygarage-remove-btn');
      button.removeAttribute('aria-label');
      button.innerHTML = '<span class="mygarage-loading-text">Saving...</span>';
    }
  }

  function restoreButtonState(button, state) {
    button.disabled = false;
    button.className = state.className;
    button.innerHTML = state.html;
    button.style.width = '';
    button.style.opacity = '';
    button.style.minWidth = '';
    button.style.maxWidth = '';

    if (state.ariaLabel) {
      button.setAttribute('aria-label', state.ariaLabel);
    } else {
      button.removeAttribute('aria-label');
    }
  }

  function getItemsInList(list) {
    return list.querySelectorAll('[data-mygarage-item]');
  }

  function updateEmptyState() {
    var lists = getGarageLists();
    var empties = getGarageEmptyStates();

    lists.forEach(function(list, index) {
      var empty = empties[index];
      if (!empty) return;

      if (getItemsInList(list).length === 0) {
        list.style.display = 'none';
        empty.style.display = '';
      } else {
        list.style.display = '';
        empty.style.display = 'none';
      }
    });
  }

  // readOnly: a list with data-mygarage-readonly (the Parts Finder sheet) shows the
  // bike without a remove button; removal happens in the Garage.
  function createGarageListItem(collectionId, title, url, readOnly) {
    var li = document.createElement('li');
    li.className = 'account__item-wrapper';
    li.setAttribute('data-mygarage-item', '');
    li.setAttribute('data-collection-id', collectionId);

    var link = document.createElement('a');
    link.className = 'account__item';
    link.href = url || '#';

    var text = document.createElement('span');
    text.className = 'account__item-text';
    text.textContent = title || 'Saved motorcycle';
    link.appendChild(text);

    li.appendChild(link);
    if (readOnly) return li;

    var button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-mygarage-toggle', '');
    button.setAttribute('data-mygarage-action', 'remove');
    button.setAttribute('data-collection-id', collectionId);
    button.className = 'mygarage-remove-btn';
    button.setAttribute('aria-label', 'Remove from MyGarage');
    button.innerHTML = getRemoveIconSVG();

    li.appendChild(document.createTextNode(' '));
    li.appendChild(button);

    return li;
  }

  function findExistingGarageItems(collectionId) {
    return Array.from(document.querySelectorAll('[data-mygarage-item][data-collection-id="' + CSS.escape(collectionId) + '"]'));
  }

  function removeItemFromAllGarageLists(collectionId) {
    var items = findExistingGarageItems(collectionId);

    items.forEach(function(item) {
      item.remove();
    });
  }

  function appendItemToAllGarageLists(collectionId, title, url) {
    var lists = getGarageLists();

    lists.forEach(function(list) {
      var existing = list.querySelector('[data-mygarage-item][data-collection-id="' + CSS.escape(collectionId) + '"]');

      if (!existing) {
        list.appendChild(createGarageListItem(collectionId, title, url, list.hasAttribute('data-mygarage-readonly')));
      }
    });
  }

  function updateToggleButtons(collectionId, action) {
    var buttons = document.querySelectorAll('[data-mygarage-toggle][data-collection-id="' + CSS.escape(collectionId) + '"]');

    buttons.forEach(function(button) {
      if (action === 'remove') {
        setButtonToRemoveState(button);
      } else {
        setButtonToSaveState(button);
      }
    });
  }

  async function sendMyGarageRequest(action, collectionId) {
    var endpoint = action === 'remove' ? '/apps/mygarage/remove' : '/apps/mygarage/save';

    var response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        collection_id: collectionId
      })
    });

    var data = await response.json();

    if (!response.ok || !data.ok) {
      throw new Error(data.error || ('Failed to ' + action + ' motorcycle'));
    }

    return data;
  }

  document.addEventListener('click', async function(event) {
    var button = event.target.closest('[data-mygarage-toggle]');
    if (!button) return;

    event.preventDefault();

    if (button.disabled) return;

    var action = button.getAttribute('data-mygarage-action');
    var collectionId = button.getAttribute('data-collection-id');

    if (!action || !collectionId) return;

    var originalState = {
      html: button.innerHTML,
      className: button.className,
      ariaLabel: button.getAttribute('aria-label')
    };

    setButtonLoadingState(button, action);

    try {
      await sendMyGarageRequest(action, collectionId);

      if (action === 'remove') {
        removeItemFromAllGarageLists(collectionId);
        updateToggleButtons(collectionId, 'save');
        updateEmptyState();
        return;
      }

      if (action === 'save') {
        updateToggleButtons(collectionId, 'remove');

        var title = button.getAttribute('data-collection-title') || button.getAttribute('data-title') || document.title;
        var url = button.getAttribute('data-collection-url') || button.getAttribute('data-url') || window.location.pathname;

        appendItemToAllGarageLists(collectionId, title, url);
        updateEmptyState();
        return;
      }

      restoreButtonState(button, originalState);
    } catch (error) {
      console.error('MyGarage request failed:', error);
      restoreButtonState(button, originalState);
      alert(error.message || 'Something went wrong.');
    }
  });

  updateEmptyState();

  // 5. Finder: year / make / model selectors that feed the MyGarage save button.
  //
  //    The data file declares  const jsonData = { years: [ { year, makes: [ { make,
  //    models: [ { name, collection_url } ] } ] } ] }.  It is large, so it is only
  //    loaded the first time "Add a motorcycle" is pressed.
  //
  //    The data has no collection id, and MyGarage saves by id, so the id and title
  //    are looked up from <collection_url>.json when a model is picked. They are put
  //    on the save button as data-collection-id / -title / -url, and from there the
  //    click is handled by section 4 exactly like any other save button.

  var SAVE_LABEL = 'Save to MyGarage';
  var collectionCache = {};

  function loadFinderData(src) {
    return new Promise(function (resolve, reject) {
      if (typeof jsonData !== 'undefined') { resolve(jsonData); return; }
      var script = document.createElement('script');
      script.src = src;
      script.onload = function () {
        if (typeof jsonData !== 'undefined') resolve(jsonData);
        else reject(new Error('Finder data did not define jsonData'));
      };
      script.onerror = function () { reject(new Error('Finder data failed to load')); };
      document.head.appendChild(script);
    });
  }

  function lookupCollection(url) {
    if (!collectionCache[url]) {
      collectionCache[url] = fetch(url + '.json', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Collection not found');
          return response.json();
        })
        .then(function (data) {
          if (!data || !data.collection || !data.collection.id) throw new Error('Collection not found');
          return data.collection;
        })
        .catch(function (error) {
          delete collectionCache[url];
          throw error;
        });
    }
    return collectionCache[url];
  }

  function fillSelect(select, placeholder, items) {
    select.innerHTML = '';
    select.appendChild(new Option(placeholder, ''));
    items.forEach(function (item) {
      select.appendChild(new Option(item.label, item.value));
    });
    select.value = '';
    select.disabled = items.length === 0;
  }

  function initFinder(garage) {
    var finder = garage.querySelector('[data-garage-finder]');
    if (!finder || finder.hasAttribute('data-garage-finder-ready')) return;
    finder.setAttribute('data-garage-finder-ready', '');

    var yearSelect = finder.querySelector('[data-garage-finder-year]');
    var makeSelect = finder.querySelector('[data-garage-finder-make]');
    var modelSelect = finder.querySelector('[data-garage-finder-model]');
    var saveButton = finder.querySelector('[data-garage-finder-save]');
    var status = finder.querySelector('[data-garage-finder-status]');
    var openButton = finder.querySelector('[data-garage-finder-open]');
    var fields = finder.querySelector('[data-garage-finder-fields]');
    if (!yearSelect || !makeSelect || !modelSelect || !saveButton) return;

    var years = [];
    var requestToken = 0;

    function setStatus(text) {
      if (status) status.textContent = text || '';
    }

    function resetSaveButton() {
      requestToken++;
      saveButton.removeAttribute('data-collection-id');
      saveButton.removeAttribute('data-collection-title');
      saveButton.removeAttribute('data-collection-url');
      saveButton.setAttribute('data-mygarage-action', 'save');
      saveButton.style.width = '';
      saveButton.textContent = SAVE_LABEL;
      saveButton.disabled = true;
    }

    function selectedYear() {
      return years.filter(function (y) { return String(y.year) === yearSelect.value; })[0];
    }

    function selectedMake() {
      var year = selectedYear();
      return year && year.makes.filter(function (m) { return m.make === makeSelect.value; })[0];
    }

    function isInGarage(collectionId) {
      return !!garage.querySelector('[data-mygarage-item][data-collection-id="' + CSS.escape(String(collectionId)) + '"]');
    }

    yearSelect.addEventListener('change', function () {
      var year = selectedYear();
      fillSelect(makeSelect, 'Make', year ? year.makes.map(function (m) { return { label: m.make, value: m.make }; }) : []);
      fillSelect(modelSelect, 'Model', []);
      resetSaveButton();
      setStatus('');
    });

    makeSelect.addEventListener('change', function () {
      var make = selectedMake();
      fillSelect(modelSelect, 'Model', make ? make.models.map(function (m) { return { label: m.name, value: m.collection_url }; }) : []);
      resetSaveButton();
      setStatus('');
    });

    modelSelect.addEventListener('change', function () {
      resetSaveButton();
      setStatus('');
      var url = modelSelect.value;
      if (!url) return;

      var token = requestToken;
      var fallbackTitle = [yearSelect.value, makeSelect.value, modelSelect.options[modelSelect.selectedIndex].text].join(' ');

      lookupCollection(url).then(function (collection) {
        if (token !== requestToken) return; // selection changed while loading
        saveButton.setAttribute('data-collection-id', String(collection.id));
        saveButton.setAttribute('data-collection-title', collection.title || fallbackTitle);
        saveButton.setAttribute('data-collection-url', url);
        if (isInGarage(collection.id)) {
          setStatus('Already in your garage.');
        } else {
          saveButton.disabled = false;
        }
      }).catch(function () {
        if (token !== requestToken) return;
        setStatus('That motorcycle could not be found. Try another.');
      });
    });

    // After a successful save, section 4 flips this button to "remove".
    // Use that as the signal to reset the finder for the next motorcycle.
    new MutationObserver(function () {
      if (saveButton.getAttribute('data-mygarage-action') !== 'remove') {
        // re-enabled because the motorcycle was just removed from the list
        if (!saveButton.disabled) setStatus('');
        return;
      }
      modelSelect.value = '';
      resetSaveButton();
      setExpanded(false);
      setStatus('Saved to your garage.');
    }).observe(saveButton, { attributes: true, attributeFilter: ['data-mygarage-action'] });

    // The data file is only fetched the first time the selectors are shown.
    var dataRequested = false;
    function loadData() {
      if (dataRequested) return;
      dataRequested = true;
      yearSelect.options[0].text = 'Loading…';
      loadFinderData(finder.getAttribute('data-garage-finder-src')).then(function (data) {
        years = (data && data.years) || [];
        fillSelect(yearSelect, 'Year', years.map(function (y) { return { label: String(y.year), value: String(y.year) }; }));
        if (fields && !fields.hidden) yearSelect.focus();
      }).catch(function (error) {
        console.error('Garage finder:', error);
        dataRequested = false; // allow another try
        yearSelect.options[0].text = 'Year';
        setStatus('The motorcycle list could not be loaded.');
      });
    }

    // "Add a motorcycle" swaps itself for the selectors; a save swaps back.
    function setExpanded(expanded) {
      if (!openButton || !fields) return;
      fields.hidden = !expanded;
      openButton.hidden = expanded;
      openButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    // Put the finder back to just "Add a motorcycle". Runs when the Garage closes
    // (called directly by closeGarage, and from the dialog's own close event as a
    // backstop) and again each time it opens.
    resetFinder = function () {
      setExpanded(false);
      if (years.length) {
        yearSelect.value = '';
        fillSelect(makeSelect, 'Make', []);
        fillSelect(modelSelect, 'Model', []);
      }
      resetSaveButton();
      setStatus('');
    };
    garage.addEventListener('close', resetFinder);

    if (openButton && fields) {
      openButton.addEventListener('click', function () {
        setStatus('');
        setExpanded(true);
        loadData();
        if (!yearSelect.disabled) yearSelect.focus();
      });
    } else {
      loadData(); // no toggle in the markup: selectors are always visible
    }
  }
})();
